import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Heart, Users, ArrowLeft, RefreshCw, Sparkles } from "lucide-react";
import DemoBanner from "./DemoBanner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const TENANT = "company-a";

export default function CompanyAEmployerDemo() {
  const [util, setUtil] = useState({ unique_employees: 0, total_submissions: 0, by_contact_type: {}, by_status: {} });
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/demo/${TENANT}/utilization`).then((r) => setUtil(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    document.title = "Company A — Live Employer Dashboard";
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const ct = util.by_contact_type || {};
  const byContact = [
    { label: "Message", value: ct.message || 0 },
    { label: "Video Request", value: ct.video_request || 0 },
  ];

  return (
    <main data-testid="company-a-employer-demo" className="bg-[#FAFBFD] min-h-screen">
      <DemoBanner title="Company A — CCCHAPS Care Portal Demonstration Environment" subtitle="Aggregate only. No employee names, contact info, or message content." />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
          <Link to="/care-portal/demo/company-a" data-testid="ca-empr-back" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" strokeWidth={2.2} /> Back to Company A
          </Link>
          <Link to="/care-portal/demo/company-a/employee" data-testid="ca-empr-to-employee" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#4A6FA5] hover:text-[#3B597B] uppercase tracking-wider">
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.4} /> Back to Employee Experience
          </Link>
        </div>

        <header className="flex items-end justify-between gap-4 pb-8 border-b border-slate-200 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <img src="/callachap-logo.png" alt="CALLACHAP" className="h-9 md:h-10 w-auto" />
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] uppercase tracking-[0.22em] font-semibold text-emerald-800">
                <Sparkles className="w-3 h-3" strokeWidth={2.4} /> Live
              </span>
            </div>
            <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Employer Utilization Dashboard</p>
            <h1 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">Company A — Demonstration</h1>
            <p className="mt-2 text-sm text-slate-600">Live aggregate from Company A demonstration activity · auto-refresh every 15 seconds</p>
          </div>
          <button onClick={load} data-testid="ca-empr-refresh" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:border-[#4A6FA5]">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={2.2} /> Refresh
          </button>
        </header>

        <section className="mt-10 grid lg:grid-cols-3 gap-5">
          <div data-testid="ca-empr-employees" className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-rose-50 text-rose-500"><Heart className="w-5 h-5" strokeWidth={2.2} /></span>
              <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Unique Employees</p>
            </div>
            <div className="font-heading text-5xl md:text-6xl text-slate-900 tracking-tight">{util.unique_employees}</div>
            <p className="mt-1 text-xs text-slate-500">Distinct emails across all demo submissions</p>
          </div>
          <div data-testid="ca-empr-submissions" className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8 lg:col-span-2">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#F0F4F8] text-[#4A6FA5]"><Users className="w-5 h-5" strokeWidth={2.2} /></span>
              <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Total Support Connections</p>
            </div>
            <div className="font-heading text-5xl md:text-6xl text-slate-900 tracking-tight">{util.total_submissions}</div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {byContact.map((c) => (
                <div key={c.label} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{c.label}</p>
                  <div className="font-heading text-2xl text-slate-900">{c.value}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mt-8 rounded-2xl p-6 md:p-7 bg-white border border-slate-200" data-testid="ca-empr-privacy-section">
          <h2 className="font-heading text-lg tracking-tight text-slate-900">Privacy by design</h2>
          <p className="mt-2 text-sm text-slate-700 leading-relaxed">
            The organization sees program utilization and aggregate patterns. It does not receive the private details of individual chaplain conversations.
          </p>
          <p className="mt-3 text-xs text-slate-500 leading-relaxed">
            Company A is a sample organization used to demonstrate CCCHAPS Care Portal workflows. Activity in this environment is isolated from live employee, employer, support-request, and production Care Portal data. Demo information is automatically removed after 30 days and may also be cleared after a presentation. This dashboard shows only aggregate counts — it never surfaces employee names, contact information, message content, chaplain notes, activity history, or individual reasons.
          </p>
        </section>
      </div>
    </main>
  );
}
